import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info, ChevronDown, ChevronUp, Target, AlertCircle } from 'lucide-react';
import { useTranslation } from '@/components/utils/translations';
import { appendAnalyticsWindow, coerceAnalyticsWindow } from '@/lib/analyticsWindow';

const API_BASE_URL = window.location.hostname === 'localhost' 
  ? 'https://api.neogtm.com' 
  : 'https://api.neogtm.com';

// Target-product filter — same shape used in SignalsTab / MostActiveTab so
// users get an identical control across tabs. Empty value = no filter.
const TARGET_PRODUCT_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'SYS', label: 'SYS' },
  { value: 'EXAM', label: 'EXAM' },
  { value: 'ALL', label: 'ALL (multi)' },
];

function TargetProductSwitch({ value, onChange }) {
  return (
    <div
      className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 self-start"
      role="group"
      aria-label="Target product filter"
    >
      {TARGET_PRODUCT_OPTIONS.map(({ value: optionValue, label }) => (
        <button
          key={optionValue || 'all'}
          type="button"
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === optionValue
              ? 'bg-white text-[#0A1A2F] shadow-sm'
              : 'text-gray-600 hover:text-[#0A1A2F]'
          }`}
          onClick={() => onChange(optionValue)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function AccountsTab({ tenantId, timeRange, analyticsWindow, userId }) {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [accountDetail, setAccountDetail] = useState(null);
  const [tierFilter, setTierFilter] = useState('All');
  const [targetProductFilter, setTargetProductFilter] = useState('');
  
  useEffect(() => {
    loadAccounts();
  }, [tenantId, timeRange, analyticsWindow, userId, tierFilter, targetProductFilter]);
  
  const loadAccounts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        days: timeRange,
        ...(tierFilter !== 'All' && { tier: tierFilter }),
        ...(targetProductFilter && { target_products: targetProductFilter }),
      });
      appendAnalyticsWindow(params, coerceAnalyticsWindow(analyticsWindow ?? timeRange));
      if (userId) params.append('user_id', userId);
      
      const response = await fetch(
        `${API_BASE_URL}/api/analytics-v2/accounts/comms-depth?${params}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccounts(data);
      }
    } catch (error) {
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadAccountDetail = async (accountId) => {
    try {
      const detailParams = new URLSearchParams({
        tenant_id: tenantId,
        days: timeRange,
        ...(targetProductFilter && { target_products: targetProductFilter }),
      });
      appendAnalyticsWindow(detailParams, coerceAnalyticsWindow(analyticsWindow ?? timeRange));
      if (userId) detailParams.append('user_id', userId);
      const response = await fetch(
        `${API_BASE_URL}/api/analytics-v2/accounts/${accountId}/detail?${detailParams.toString()}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAccountDetail(data);
      }
    } catch (error) {
      console.error('Error loading account detail:', error);
    }
  };

  useEffect(() => {
    if (expandedAccount) loadAccountDetail(expandedAccount);
  }, [targetProductFilter]);
  
  const toggleExpand = (accountId) => {
    if (expandedAccount === accountId) {
      setExpandedAccount(null);
      setAccountDetail(null);
    } else {
      setExpandedAccount(accountId);
      loadAccountDetail(accountId);
    }
  };
  
  const getDepthColor = (score) => {
    if (score >= 70) return 'bg-green-500';
    if (score >= 40) return 'bg-yellow-500';
    return 'bg-red-400';
  };

  const formatRate = (rate, sends) => {
    const sendsNum = Number(sends || 0);
    const rateNum = Number(rate);
    if (!Number.isFinite(rateNum) || sendsNum <= 0) return '—';
    return `${rateNum.toFixed(1)}%`;
  };
  
  return (
    <div className="space-y-6">
      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Target product:</span>
              <TargetProductSwitch value={targetProductFilter} onChange={setTargetProductFilter} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('filterByTier')}:</span>
              <Select value={tierFilter} onValueChange={setTierFilter}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">{t('allTiers')}</SelectItem>
                  <SelectItem value="T1">T1</SelectItem>
                  <SelectItem value="T2">T2</SelectItem>
                  <SelectItem value="T3">T3</SelectItem>
                  <SelectItem value="T4">T4</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Accounts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-[#00C2C2]" />
            {t('accountCommunicationsDepth')}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-4 h-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">
                    {t('depthScoreTooltip')}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#00C2C2] border-t-transparent rounded-full"></div>
            </div>
          ) : accounts.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-gray-500">
              {t('noAccountDataSelectedPeriod')}
            </div>
          ) : (
            <div className="space-y-2">
              {accounts.map((account) => (
                <div key={account.account_id} className="border rounded-lg">
                  <div 
                    className="p-3 md:p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleExpand(account.account_id)}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="w-8 flex-shrink-0">
                        {expandedAccount === account.account_id ? 
                          <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{account.account_name}</div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-[10px] md:text-xs">{account.account_tier}</Badge>
                          {account.archetypes_covered.slice(0, 2).map(arch => (
                            <Badge key={arch} variant="secondary" className="text-[10px] md:text-xs">{arch}</Badge>
                          ))}
                          {account.archetypes_covered.length > 2 && (
                            <Badge variant="secondary" className="text-[10px] md:text-xs">
                              +{account.archetypes_covered.length - 2} {t('more')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4 md:gap-8 justify-between md:justify-end flex-shrink-0 w-full md:w-auto">
                      <div className="text-center flex-shrink-0">
                        <div className="text-[10px] md:text-xs text-gray-500">{t('depth')}</div>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${getDepthColor(account.depth_score)}`}></div>
                          <span className="font-semibold text-xs md:text-sm">{account.depth_score}</span>
                        </div>
                      </div>
                      <div className="text-center flex-shrink-0">
                        <div className="text-[10px] md:text-xs text-gray-500">{t('personas')}</div>
                        <span className="font-semibold text-xs md:text-sm">{account.personas_touched}</span>
                      </div>
                      <div className="text-center flex-shrink-0">
                        <div className="text-[10px] md:text-xs text-gray-500">{t('replies')}</div>
                        <span className="font-semibold text-xs md:text-sm">{account.replies}</span>
                      </div>
                      <div className="text-center flex-shrink-0">
                        <div className="text-[10px] md:text-xs text-gray-500">{t('playbooks')}</div>
                        <span className="font-semibold text-xs md:text-sm">{account.active_playbooks}</span>
                      </div>
                      <div className="text-center flex-shrink-0">
                        <div className="text-[10px] md:text-xs text-gray-500 inline-flex items-center gap-1">
                          Open
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="w-3 h-3 text-gray-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">
                                  Scanner-filtered: opens/clicks where the user-agent and IP were classified as a real human.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <span className="font-semibold text-xs md:text-sm">{formatRate(account.open_rate, account.email_sends)}</span>
                      </div>
                      <div className="text-center flex-shrink-0">
                        <div className="text-[10px] md:text-xs text-gray-500">Click</div>
                        <span className="font-semibold text-xs md:text-sm">{formatRate(account.click_rate, account.email_sends)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Detail */}
                  {expandedAccount === account.account_id && accountDetail && (
                    <div className="border-t p-3 md:p-4 bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        {/* Coverage Matrix */}
                        <div className="min-w-0">
                          <h4 className="font-medium mb-2 md:mb-3 text-xs md:text-sm">{t('archetypeCoverage')}</h4>
                          <div className="space-y-2">
                            {Object.entries(accountDetail.coverage_matrix).map(([arch, status]) => (
                              <div key={arch} className="flex items-center justify-between p-2 bg-white rounded border min-w-0">
                                <span className="text-xs md:text-sm truncate min-w-0">{arch}</span>
                                <div className="flex gap-2 flex-shrink-0">
                                  <Badge variant={status.touched ? 'default' : 'outline'} 
                                         className={`text-[10px] md:text-xs ${status.touched ? 'bg-blue-100 text-blue-800' : ''}`}>
                                    {status.touched ? t('touched') : t('notTouched')}
                                  </Badge>
                                  {status.replied && (
                                    <Badge className="bg-green-100 text-green-800 text-[10px] md:text-xs">{t('replied')}</Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Suggestions */}
                        <div className="min-w-0">
                          <h4 className="font-medium mb-2 md:mb-3 text-xs md:text-sm">{t('recommendations')}</h4>
                          {accountDetail.suggestions.length > 0 ? (
                            <div className="space-y-2">
                              {accountDetail.suggestions.map((suggestion, idx) => (
                                <div key={idx} className="flex items-start gap-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                                  <AlertCircle className="w-3 h-3 md:w-4 md:h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                                  <span className="text-xs md:text-sm break-words min-w-0">{suggestion}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-xs md:text-sm text-green-600">{t('goodCoverage')}</div>
                          )}
                          
                          {/* Recent Timeline */}
                          {accountDetail.timeline.length > 0 && (
                            <div className="mt-4">
                              <h4 className="font-medium mb-2 text-xs md:text-sm">{t('recentActivity')}</h4>
                              <div className="max-h-48 overflow-y-auto space-y-1">
                                {accountDetail.timeline.slice(0, 10).map((item) => (
                                  <div key={item.id} className="text-[10px] md:text-xs p-2 bg-white rounded border flex justify-between gap-2">
                                    <span className="truncate min-w-0 break-words">{item.person_name} ({item.channel})</span>
                                    <span className={`flex-shrink-0 ${item.replied ? 'text-green-600' : 'text-gray-500'}`}>
                                      {item.replied ? t('replied') : t('sent')}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}